import { ChevronRight, ShoppingBag } from 'lucide-react';

/**
 * The two labels the ecom entry points use, shared by the mobile banner and the
 * desktop line so the same commerce state never gets two different names across
 * breakpoints. A product you can buy is "available to shop"; one that only
 * offers a sample has "samples available to order".
 */
export const SHOP_ENTRY_LABEL = 'Available to shop on ArchiPro';
export const SAMPLE_ENTRY_LABEL = 'Samples available to order on ArchiPro';

export interface ProductShopBannerProps {
    /** Marketplace, in stock, selected variant purchasable. */
    isBuyable: boolean;
    /** A sample can be ordered (independent of full buyability). */
    isSampleBuyable: boolean;
    /** Why the selected variant cannot go in the cart, or null when it can. */
    stockNotice?: string | null;
    /** Opens the shop sheet (the variant sheet, footer led by the ecom CTA). */
    onOpen: () => void;
}

/**
 * The opt-in ecom entry point on the mobile PDP. Enquiry is the page's primary
 * action there (QDD) on every product, buyable or not, so this banner is where
 * the minority of products you can buy or sample keep that path one tap away
 * without competing with Enquire for the primary slot. Tapping opens the
 * variant sheet with its ecom footer (quantity + Add to Cart / Order Sample).
 *
 * Names the state the same way desktop does — {@link SHOP_ENTRY_LABEL} when the
 * product is buyable, {@link SAMPLE_ENTRY_LABEL} when only a sample is — and
 * the subtitle names the sample on a product that offers both, mirroring the
 * desktop sample line under Shop Now.
 *
 * Shares the revamp's editorial row language — the circular badge, ink/grey
 * type and hairline divider used by the supplier trust row beside it — rather
 * than a boxed callout. Self-hides when there is nothing to transact.
 */
export const ProductShopBanner = ({
    isBuyable,
    isSampleBuyable,
    stockNotice,
    onOpen,
}: ProductShopBannerProps) => {
    if (!isBuyable && !isSampleBuyable) return null;

    // Buyable leads with the full purchase; sample-only offers the sample. A
    // product that does both names the sample too, so it stays visible here the
    // way the desktop sample line keeps it visible under Shop Now.
    // An empty variant says so here too, rather than promising a checkout.
    const subtitle = !isBuyable
        ? 'Order a sample, delivered to your door'
        : stockNotice
          ? stockNotice
          : isSampleBuyable
            ? 'Add to cart, or order a sample first'
            : 'Add to cart and check out securely';

    return (
        <button
            type="button"
            onClick={onOpen}
            className="group flex w-full items-center gap-3 py-4 text-left"
        >
            <span className="flex size-12 shrink-0 items-center justify-center rounded-full border border-black/10 text-[#1f1f21]">
                <ShoppingBag className="size-[1.15rem]" strokeWidth={1.5} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="font-sans text-base leading-[1.2] font-medium tracking-[-0.02em] text-[#1f1f21]">
                    {isBuyable ? SHOP_ENTRY_LABEL : SAMPLE_ENTRY_LABEL}
                </span>
                <span className="font-sans text-[0.8125rem] leading-[1.2] text-[#767676]">
                    {subtitle}
                </span>
            </span>
            <ChevronRight className="size-5 shrink-0 text-[#767676] transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
        </button>
    );
};

/**
 * The desktop sample line: a single quiet line under the Shop Now / Visit
 * Website / Save cluster, for products that are both buyable and sampleable.
 * Shop Now holds the primary slot there, so without this the sample path would
 * be invisible until the drawer opens. Clicking it opens the same purchase
 * drawer, whose footer carries Order Sample alongside Add to Cart.
 *
 * Desktop needs no shop line for the other states — its primary CTA already
 * says Shop Now or Shop Sample, and a line would repeat it. Mobile still shows
 * {@link ProductShopBanner} for all of them because Enquire holds its primary
 * slot; both surfaces use {@link SAMPLE_ENTRY_LABEL} for the same state.
 */
export const ProductSampleLine = ({
    onOpen,
}: {
    /** Opens the purchase drawer, whose footer carries Order Sample. */
    onOpen: () => void;
}) => (
    <button
        type="button"
        onClick={onOpen}
        className="group inline-flex items-center gap-2 font-sans text-sm text-[#1f1f21]"
    >
        <ShoppingBag
            className="size-4 shrink-0 text-[#1f1f21]"
            strokeWidth={1.5}
        />
        <span className="leading-[1.2] font-medium tracking-[-0.01em]">
            {SAMPLE_ENTRY_LABEL}
        </span>
        <ChevronRight className="size-4 shrink-0 text-[#767676] transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
    </button>
);
